import {
  FormAnswerDocumentStatusEnum,
  FormTemplateQuestionFieldsKindEnum,
  TaskCompletion,
} from 'graphql-common';

export type CompletionDocumentsReadStatus =
  | { kind: 'none' }
  | { kind: 'all-read' }
  | { kind: 'unread'; unreadCount: number };

/**
 * Aggregate read/unread status across DOCUMENT-kind answers on a completion,
 * driven by the per-option `resp[].status` snapshot persisted by the BE at
 * `taskCompletionCreate` time (`status`/`statusReason`/
 * `documentationVersionViewId` on `FormAnswerResp` output).
 *
 * Counting rules:
 * - `READ` and `READ_PREVIOUSLY` count as read (user satisfied the option).
 * - `UNAVAILABLE` counts as read (user couldn't read — not a skip).
 * - `UNREAD` (and any missing/null status) counts as unread.
 *
 * Returns:
 * - `none`     — no DOCUMENT answers on this completion (renders `—`).
 * - `all-read` — every option resolved to a satisfied status.
 * - `unread`   — `unreadCount` options resolved to `UNREAD` (or had no status).
 */
export default function getCompletionDocumentsReadStatus(
  answers: TaskCompletion['answers'],
): CompletionDocumentsReadStatus {
  const documentAnswers = (answers || []).filter(
    (a) => a?.kind === FormTemplateQuestionFieldsKindEnum.Document,
  );

  let totalOptions = 0;
  let unreadCount = 0;

  documentAnswers.forEach((answer) => {
    (answer?.resp || []).forEach((r) => {
      totalOptions += 1;
      const isSatisfied =
        r?.status === FormAnswerDocumentStatusEnum.Read ||
        r?.status === FormAnswerDocumentStatusEnum.ReadPreviously ||
        r?.status === FormAnswerDocumentStatusEnum.Unavailable;
      if (!isSatisfied) unreadCount += 1;
    });
  });

  if (totalOptions === 0) return { kind: 'none' };
  if (unreadCount === 0) return { kind: 'all-read' };
  return { kind: 'unread', unreadCount };
}
